<template>
    <RightSidebar :visible="isSidebarVisible" :is-sidebar-pulsing="isSidebarPulsing">
        <div class="flex flex-col gap-4 items-center">
            <div class="w-full flex justify-end">
                <XMarkIcon class="size-5 text-won-gray cursor-pointer" @click="closeSidebarHandler" />
            </div>

            <!-- Current Stage -->
            <div class="relative w-full current-stage-job-search-plan">
                <div class="absolute top-0 right-7">
                    <div
                        class="text-white font-bold capitalize text-small bg-new-label-gradient rounded-md px-1 py-0.5"
                    >
                        beta
                    </div>
                </div>
                <div class="pt-4">
                    <h3 class="text-center text-[20px] font-semibold text-won-blue">Job Search Plan</h3>
                </div>
            </div>

            <SidebarCard class="current-stage-div">
                <!-- Header Section -->
                <div class="flex items-center justify-between">
                    <ChevronLeftIcon
                        class="size-5 text-won-ash-gray cursor-pointer job-search-plan-left-stage-arrow"
                        @click="previousStepHandler"
                    />
                    <span class="text-[18px] text-won-green font-semibold text-center select-none max-w-[96px]">
                        {{ stepData?.title || "error" }}
                    </span>
                    <ChevronRightIcon
                        class="size-5 text-won-ash-gray cursor-pointer job-search-plan-right-stage-arrow"
                        @click="nextStepHandler"
                    />
                </div>

                <!-- Progress Bar Section -->
                <ProgressBar
                    :current-step-index="currentStepIndex"
                    :viewing-step-index="stepIndex"
                    :steps="stepsArray"
                />

                <span class="text-sm leading-5 text-won-blue text-center">
                    {{ stepIndex + 1 }} of {{ stepsArray.length }}
                </span>

                <div
                    @click="showModalHandler"
                    class="flex justify-center items-between text-center space-x-0.5 cursor-pointer job-search-plan-learn-more"
                >
                    <span class="text-xs font-semibold font-xs text-won-blue">Learn more about this stage</span>
                    <ArrowRightIcon class="size-4 text-won-green" />
                </div>
            </SidebarCard>

            <!-- Content -->
            <component
                :is="stepData?.component"
                v-bind="
                    stepIndex === 0
                        ? {
                              isCycleRestarted: isCycleRestarted,
                              isStepPassed: stepIndex < currentStepIndex,
                          }
                        : {
                              isStepPassed: stepIndex < currentStepIndex,
                              isCurrentStep: stepIndex === currentStepIndex,
                          }
                "
                @open-feature="openLinkHandler"
            />
        </div>
    </RightSidebar>

    <!--   Modal   -->
    <component
        :is="stepData?.modal"
        :open-feature-link="stepData?.openFeatureLink"
        :open-feature-param="stepData?.openFeatureParam"
        :show-modal="showModal"
        @close-modal="closeModalHandler"
    >
    </component>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";

import { useJobSearchPlanStore } from "../../store/job-search-plan.store";
import { COMPONENTS_MAP, stepsArray } from "../../utils/job-search-plans";
import { isJobTrackerEnabled } from "../../utils/helpers";
import { startFireworks } from "../../utils/fireworks";

import RightSidebar from "../right-sidebar.vue";
import ProgressBar from "./progress-bar.vue";
import SidebarCard from "./sidebar-card.vue";

import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/vue/20/solid";

const jobSearchPlanStore = useJobSearchPlanStore();
const { isSidebarVisible, isCycleRestarted, steps, currentStep, currentStepIndex, showModal, isMovedToNextStage } =
    storeToRefs(jobSearchPlanStore);

const router = useRouter();
const route = useRoute();

const isJobTrackerEnabledCopy = isJobTrackerEnabled();

const viewingStep = ref(currentStep.value);
const stepIndex = computed(() => steps.value.indexOf(viewingStep.value));
const stepData = computed(() => COMPONENTS_MAP[viewingStep.value]);
const isSidebarPulsing = ref(false);

const openLinkHandler = () => {
    if (isJobTrackerEnabledCopy) {
        router.push({
            name: stepData.value.openFeatureLink,
            ...(stepData.value.openFeatureParam ? { params: { id: stepData.value.openFeatureParam } } : {}),
        });
    } else {
        router.push({
            name: "job-tracker",
        });
    }
};

const closeSidebarHandler = () => {
    isSidebarVisible.value = false;

    if (isSidebarPulsing.value) {
        isSidebarPulsing.value = false;
    }
};

const nextStepHandler = () => {
    if (stepIndex.value + 1 < steps.value.length) {
        viewingStep.value = steps.value[stepIndex.value + 1];
    }
};

const previousStepHandler = () => {
    if (stepIndex.value > 0) {
        viewingStep.value = steps.value[stepIndex.value - 1];
    }
};

const showModalHandler = () => {
    showModal.value = true;
};

const closeModalHandler = () => {
    showModal.value = false;
};

const mountSidebar = async () => {
    if (route.name !== "job-board-search") {
        await jobSearchPlanStore.refresh();
    }
    
    isSidebarPulsing.value = isMovedToNextStage.value;

    if (isMovedToNextStage.value) {
        isSidebarVisible.value = true;
        startFireworks();

        jobSearchPlanStore.resetStage();
    }
};

watch(currentStep, (newStep) => {
    viewingStep.value = newStep;
});

watch(route, async () => {
    isSidebarVisible.value = false;
    await mountSidebar();
});

watch(isSidebarVisible, async () => {
    if (isSidebarVisible.value) {
        await mountSidebar();
    }
});

onMounted(async () => {
    await mountSidebar();
});
</script>
